/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import {
  DataGridPro,
  DataGridProProps,
  GridCallbackDetails,
  GridColDef,
  GridEventListener,
  GridPinnedColumns,
  GridRenderCellParams,
  GridRowIdGetter,
  GridRowSelectionModel,
  GridSlotsComponent,
  GridSortDirection,
  GridSortModel,
  gridFilteredDescendantCountLookupSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import { Box, Button, ButtonProps, SxProps, Typography } from "@mui/material";
import { useStoreDispatch } from "../../../store/hooks";
import { setDataGridSort, setPaginationModel } from "../../../store/helpers/helperReducers";
import { useSelector } from "react-redux";
import { paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { GridSlotsComponentsProps, UncapitalizeObjectKeys } from "@mui/x-data-grid/internals";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { IPagination } from "../../../store/helpers/types";


// ── Grid dimension constants ──
const ROW_HEIGHT = 52;
const HEADER_HEIGHT = 52;
const FOOTER_HEIGHT = 48;

const getGridHeight = (rowCount: number, pageSize: number, hideFooter: boolean): number => {
  const visibleRows = Math.max(rowCount, pageSize);
  return HEADER_HEIGHT + visibleRows * ROW_HEIGHT + (hideFooter ? 0 : FOOTER_HEIGHT);
};

interface ITreeDataGridProProps {
  tableId: string;
  sx?: SxProps;
  columns: GridColDef<any>[];
  rows: any[];
  rowCount?: number | undefined;
  loading?: boolean | undefined;
  autoHeight?: boolean | undefined;
  checkboxSelection?: boolean | undefined;
  onRowClick?: GridEventListener<"rowClick"> | undefined;
  sortingOrder?: GridSortDirection[] | undefined;
  onSortModelChange?: ((model: GridSortModel, details: GridCallbackDetails<any>) => void) | undefined;
  getRowId?: GridRowIdGetter<any> | undefined;
  disableRowSelectionOnClick?: boolean | undefined;
  slots?: UncapitalizeObjectKeys<Partial<GridSlotsComponent>> | undefined;
  slotProps?: GridSlotsComponentsProps | undefined;
  disableColumnMenu?: boolean | undefined;
  onRowSelectionModelChange?: ((rowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails<any>) => void) | undefined;
  pinnedColumns?: GridPinnedColumns | undefined
  hideFooter?: boolean | undefined;
  groupHeaderName?: string | undefined;
  fillContainer?: boolean | undefined;
}

export const isNavigationKey = (key: string) =>
  key === 'Home' ||
  key === 'End' ||
  key.indexOf('Arrow') === 0 ||
  key.indexOf('Page') === 0 ||
  key === ' ';

function CustomGridTreeDataGroupingCell(props: GridRenderCellParams) {
  const { id, rowNode, formattedValue } = props;
  const apiRef = useGridApiContext();
  const filteredDescendantCountLookup = useGridSelector(
    apiRef,
    gridFilteredDescendantCountLookupSelector,
  );
  const filteredDescendantCount = filteredDescendantCountLookup[rowNode.id] ?? 0;

  const handleClick: ButtonProps['onClick'] = (event) => {
    if (rowNode.type !== 'group') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    (event as any).defaultMuiPrevented = true;
    apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
  };
  return (
    <Box sx={{ ml: rowNode.depth * 4 }}>
      <div>
        {filteredDescendantCount > 0 ? (
          <Button sx={{ textTransform: "none", color: "var(--foreground)" }} startIcon={rowNode.type === "group" && rowNode.childrenExpanded ? <RemoveIcon /> : <AddIcon />} onClick={handleClick} tabIndex={-1} size="small">
            {/* {`${formattedValue}(${filteredDescendantCount})`} */}
            {formattedValue}
          </Button>
        ) : (
          <Typography sx={{ fontSize: "0.8125rem", pl: 3, color: "var(--foreground)" }} variant="subtitle2" gutterBottom>{rowNode.type === "leaf" ? rowNode?.groupingKey : ""}</Typography>
        )}
      </div>
    </Box>
  );
}


const CustomTreeDataGridPro: React.FC<ITreeDataGridProProps> = ({
  tableId = "customTreeTable",
  sx,
  rows,
  columns,
  rowCount,
  loading,
  sortingOrder,
  getRowId,
  onRowClick,
  checkboxSelection,
  //pageSizeOptions,
  onSortModelChange,
  disableRowSelectionOnClick,
  slots,
  slotProps,
  autoHeight,
  disableColumnMenu,
  onRowSelectionModelChange,
  pinnedColumns,
  hideFooter = false,
  groupHeaderName,
  fillContainer = false,
}) => {
  const dispatch = useStoreDispatch();

  const paginationModel = useSelector(paginationModelSelector);

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel.length > 0) {
        const sortedField: string = Object.values(sortModel[0]).toString();
        dispatch(setDataGridSort({ [tableId]: sortedField }));
      }
    },
    [dispatch, tableId]
  );

  const getTreeDataPath: DataGridProProps['getTreeDataPath'] = React.useCallback((row: any) => row.hierarchy, []);

  const groupingColDef: DataGridProProps['groupingColDef'] = React.useMemo(() => ({
    headerName: groupHeaderName || 'Group',
    renderCell: (params) => <CustomGridTreeDataGroupingCell {...params} />,
  }), [groupHeaderName]);

  const darkModeSx: SxProps = {
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    overflow: "hidden",
    backgroundColor: "var(--card)",
    color: "var(--foreground)",

    "& .MuiDataGrid-overlayWrapper": { minHeight: 55 },
    "& .MuiDataGrid-overlay": { backgroundColor: "var(--card)", color: "var(--muted-foreground)" },

    "& .MuiDataGrid-row": {
      borderBottom: "1px solid var(--border)",
      transition: "background-color 150ms cubic-bezier(0.4,0,0.2,1)",
      "&:hover": { backgroundColor: "var(--muted)" },
      "&:last-child": { borderBottom: "none" },
    },

    "& .MuiDataGrid-cell": {
      borderBottom: "none",
      color: "var(--foreground)",
      padding: "0 16px",
    },
    "& .MuiDataGrid-cell .MuiDataGrid-cellContent": { color: "inherit" },
    "& .MuiDataGrid-cell .MuiTypography-root": { color: "inherit" },

    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 500,
      color: "var(--muted-foreground)",
      textTransform: "none",
    },
    "& .MuiDataGrid-columnHeaders": {
      borderBottom: "1px solid var(--border)",
      backgroundColor: "var(--card)",
      color: "var(--muted-foreground)",
    },
    "& .MuiDataGrid-columnHeader": {
      "&:hover": { backgroundColor: "transparent !important" },
      "&:focus": { outline: "none" },
      "&:focus-within": { outline: "none" },
    },
    "& .MuiDataGrid-columnSeparator": { display: "none" },

    "& .MuiDataGrid-footerContainer": {
      borderTop: "1px solid var(--border)",
      backgroundColor: "var(--card)",
      color: "var(--muted-foreground)",
    },
    "& .MuiTablePagination-root": { color: "var(--muted-foreground)" },
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { color: "var(--muted-foreground)" },
    "& .MuiTablePagination-select": { color: "var(--foreground)" },

    "& .MuiDataGrid-pinnedColumnHeaders": { backgroundColor: "var(--card)" },
    "& .MuiDataGrid-pinnedColumns": { backgroundColor: "var(--card)" },

    "& .MuiIconButton-root": { color: "var(--muted-foreground)" },
    "& .MuiCheckbox-root": { color: "var(--muted-foreground)" },
  };

  const mergedSx = { ...darkModeSx, ...(sx as any) };

  const apiRef = useGridApiRef();
  const minPageSize = 5;
  const actualRows = rows?.length || 0;
  const computedHeight = getGridHeight(actualRows, minPageSize, hideFooter);
  const actualRowsHeight = HEADER_HEIGHT + Math.min(actualRows, minPageSize) * ROW_HEIGHT + (hideFooter ? 0 : FOOTER_HEIGHT);
  const minRowsHeight = HEADER_HEIGHT + minPageSize * ROW_HEIGHT + (hideFooter ? 0 : FOOTER_HEIGHT);

  const [isAnyExpanded, setIsAnyExpanded] = React.useState(false);

  const checkExpansion = React.useCallback(() => {
    if (!apiRef.current?.state?.rows?.tree) return false;
    return Object.values(apiRef.current.state.rows.tree).some(
      (node: any) => node?.type === "group" && node?.childrenExpanded && node?.depth > -1
    );
  }, [apiRef]);

  React.useEffect(() => {
    return apiRef.current.subscribeEvent("rowExpansionChange", () => {
      setIsAnyExpanded(checkExpansion());
    });
  }, [apiRef, checkExpansion]);

  React.useEffect(() => {
    setIsAnyExpanded(checkExpansion());
  }, [rows, checkExpansion]);

  const gridWrapperRef = React.useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = React.useState(0);

  React.useEffect(() => {
    const measure = () => {
      if (gridWrapperRef.current) {
        const rect = gridWrapperRef.current.getBoundingClientRect();
        setAvailableHeight(window.innerHeight - rect.top - 16);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [fillContainer]);
  
  const expansionHeight = isAnyExpanded ? computedHeight : actualRowsHeight;
  
  const gridHeight = fillContainer
    ? Math.max(availableHeight, expansionHeight, minRowsHeight)
    : expansionHeight;

  return (
    <div ref={gridWrapperRef}>
      <DataGridPro
        treeData
        apiRef={apiRef}
        sx={{ height: gridHeight, ...mergedSx }}
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        loading={loading}
        getRowId={getRowId}
        autoHeight={autoHeight}
        disableColumnMenu={disableColumnMenu}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={paginationModel[tableId]}
        paginationMode="server"
        sortingMode="server"
        sortingOrder={sortingOrder || ["desc", "asc"]}
        onPaginationModelChange={(newValue: IPagination) => dispatch(setPaginationModel({ [tableId]: newValue }))}
        onSortModelChange={handleSortModelChange}
        onRowClick={onRowClick}
        slots={slots}
        slotProps={{
          basePopper: { sx: { "& .MuiPaper-root": { backgroundColor: "var(--card)" } } },
        }}
        initialState={{
          pagination: {
            paginationModel,
          },
          pinnedColumns,
        }}
        onRowSelectionModelChange={onRowSelectionModelChange}
        getTreeDataPath={getTreeDataPath}
        groupingColDef={groupingColDef}
        hideFooter={hideFooter}
      />
    </div>
  );
};

export default CustomTreeDataGridPro;
